/**
 * TEP (Trinetra Evaluated Posture) settings tab.
 *
 * Org admins pick:
 *   - jurisdictions (country/region tags) — drives which frameworks
 *     show up by default in the picker.
 *   - active frameworks — the list TEP snapshots are built against.
 *
 * Frameworks are sourced from the SCF catalog seeded by the platform
 * admin; the UI doesn't try to invent framework metadata locally.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, ShieldCheck, AlertCircle, ExternalLink, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FrameworkRow {
  frameworkId: string;
  name: string;
  shortName: string;
  jurisdiction: string;
  category: 'standard' | 'regulation' | 'industry';
  description: string | null;
  url: string | null;
}

interface TepConfigRow {
  jurisdictions: string[];
  activeFrameworks: string[];
  scfVersion: string | null;
  effectiveScfVersion: string | null;
}

// Display labels for jurisdiction tags. ISO-3166 alpha-2 (lowercase),
// 'global' for jurisdiction-neutral standards, 'eu' for the EU bloc.
// Codes that aren't here fall back to the raw uppercase tag in the UI.
const JURISDICTION_LABELS: Record<string, string> = {
  global: 'Global / multilateral',
  us: 'United States',
  eu: 'European Union',
  uk: 'United Kingdom',
  // EMEA
  at: 'Austria', be: 'Belgium', de: 'Germany', gr: 'Greece', hu: 'Hungary',
  ie: 'Ireland', il: 'Israel', it: 'Italy', ke: 'Kenya', ng: 'Nigeria',
  no: 'Norway', pl: 'Poland', qa: 'Qatar', ru: 'Russia', sa: 'Saudi Arabia',
  rs: 'Serbia', za: 'South Africa', es: 'Spain', ch: 'Switzerland',
  tr: 'Turkey', ae: 'United Arab Emirates',
  // APAC
  au: 'Australia', cn: 'China', hk: 'Hong Kong', in: 'India', jp: 'Japan',
  my: 'Malaysia', nz: 'New Zealand', ph: 'Philippines', sg: 'Singapore',
  kr: 'South Korea', tw: 'Taiwan',
  // Americas
  ar: 'Argentina', bs: 'Bahamas', bm: 'Bermuda', br: 'Brazil', ca: 'Canada',
  cl: 'Chile', co: 'Colombia', mx: 'Mexico',
};

export function TepTab({ orgId }: { orgId: string }) {
  const qc = useQueryClient();

  const { data: fwRes, isLoading: fwLoading } = useQuery({
    queryKey: ['org', orgId, 'tep', 'frameworks'],
    queryFn: () => apiClient.listTepFrameworks(orgId),
  });
  const { data: cfgRes, isLoading: cfgLoading } = useQuery({
    queryKey: ['org', orgId, 'tep', 'config'],
    queryFn: () => apiClient.getTepConfig(orgId),
  });

  const frameworks = (fwRes?.data as FrameworkRow[] | undefined) ?? [];
  const cfg = cfgRes?.data as TepConfigRow | undefined;

  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [activeFrameworks, setActiveFrameworks] = useState<string[]>([]);
  const [jSearch, setJSearch] = useState('');
  const [fSearch, setFSearch] = useState('');
  // "Show only selected" defaults to ON when the org already has
  // selections — landing on TEP settings should highlight what they
  // already chose, not 250 distractor frameworks. Flips OFF on first
  // load if there are no selections yet.
  const [jOnlySelected, setJOnlySelected] = useState(false);
  const [fOnlySelected, setFOnlySelected] = useState(false);
  const normJSearch = jSearch.trim().toLowerCase();
  const normFSearch = fSearch.trim().toLowerCase();

  useEffect(() => {
    if (cfg) {
      const js = cfg.jurisdictions ?? [];
      const fs = cfg.activeFrameworks ?? [];
      setJurisdictions(js);
      setActiveFrameworks(fs);
      setJOnlySelected(js.length > 0);
      setFOnlySelected(fs.length > 0);
    }
  }, [cfg]);

  // Available jurisdictions are derived purely from what's actually
  // attached to frameworks in the catalog. 'global' is just another
  // jurisdiction here — picking 'global' shows global standards, not
  // everything-plus-global as before.
  const availableJurisdictions = useMemo(() => {
    const set = new Set<string>();
    for (const f of frameworks) if (f.jurisdiction) set.add(f.jurisdiction);
    return Array.from(set).sort((a, b) => (JURISDICTION_LABELS[a] ?? a).localeCompare(JURISDICTION_LABELS[b] ?? b));
  }, [frameworks]);

  // Search applies to both rails. For the chip rail we match against
  // the display label or the raw code; for the framework grid we match
  // against name / shortName / jurisdiction label / frameworkId.
  const visibleJurisdictions = useMemo(() => {
    let list = availableJurisdictions;
    if (jOnlySelected && jurisdictions.length > 0) {
      list = list.filter((j) => jurisdictions.includes(j));
    }
    if (normJSearch) {
      list = list.filter((j) =>
        (JURISDICTION_LABELS[j] ?? j).toLowerCase().includes(normJSearch) || j.toLowerCase().includes(normJSearch),
      );
    }
    return list;
  }, [availableJurisdictions, jurisdictions, jOnlySelected, normJSearch]);

  const visibleFrameworks = useMemo(() => {
    let list = frameworks;
    // Strict jurisdiction filter — selected chips define the allowed
    // jurisdictions exactly. 'global' is no longer auto-included; if
    // the user wants global standards they pick the chip explicitly.
    if (jurisdictions.length > 0) {
      list = list.filter((f) => jurisdictions.includes(f.jurisdiction));
    }
    if (fOnlySelected && activeFrameworks.length > 0) {
      list = list.filter((f) => activeFrameworks.includes(f.frameworkId));
    }
    if (normFSearch) {
      list = list.filter((f) =>
        f.name.toLowerCase().includes(normFSearch) ||
        f.shortName.toLowerCase().includes(normFSearch) ||
        f.frameworkId.toLowerCase().includes(normFSearch) ||
        (JURISDICTION_LABELS[f.jurisdiction] ?? f.jurisdiction).toLowerCase().includes(normFSearch),
      );
    }
    return list;
  }, [frameworks, jurisdictions, activeFrameworks, fOnlySelected, normFSearch]);

  const dirty = useMemo(() => {
    if (!cfg) return false;
    const a = jurisdictions.slice().sort().join('|');
    const b = (cfg.jurisdictions ?? []).slice().sort().join('|');
    const c = activeFrameworks.slice().sort().join('|');
    const d = (cfg.activeFrameworks ?? []).slice().sort().join('|');
    return a !== b || c !== d;
  }, [cfg, jurisdictions, activeFrameworks]);

  const save = useMutation({
    mutationFn: () => apiClient.updateTepConfig(orgId, { jurisdictions, activeFrameworks }),
    onSuccess: () => {
      toast.success('TEP settings saved');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'tep'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function toggleJurisdiction(j: string) {
    setJurisdictions((prev) => prev.includes(j) ? prev.filter((x) => x !== j) : [...prev, j]);
  }
  function toggleFramework(id: string) {
    setActiveFrameworks((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-india-green shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold mb-1">Trinetra Evaluated Posture</p>
            <p className="text-xs text-muted-foreground">
              Pick the jurisdictions and compliance frameworks this org cares about. Trinetra maps your scan findings to{' '}
              <a className="underline" href="https://securecontrolsframework.com" target="_blank" rel="noreferrer">SCF controls</a>{' '}
              and through SCF to the framework clauses you select. Posture is built on demand — open the TEP page and trigger a snapshot.
            </p>
            {cfg?.effectiveScfVersion && (
              <p className="text-[11px] text-muted-foreground font-mono mt-2">
                SCF version in use: <span className="text-foreground">{cfg.effectiveScfVersion}</span>
              </p>
            )}
            {!cfg?.effectiveScfVersion && (
              <p className="text-[11px] text-yellow-700 dark:text-yellow-400 mt-2 inline-flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" /> No active SCF version yet — ask a platform admin to load one.
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jurisdictions</p>
            <p className="text-xs text-muted-foreground">Optional. Each chip is its own jurisdiction — including <em>Global / multilateral</em>.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {jurisdictions.length > 0 && (
              <label className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={jOnlySelected}
                  onChange={(e) => setJOnlySelected(e.target.checked)}
                  className="rounded border-border"
                />
                only selected
              </label>
            )}
            {jurisdictions.length > 0 && (
              <button
                type="button"
                onClick={() => setJurisdictions([])}
                className="text-[11px] text-muted-foreground hover:text-foreground shrink-0"
              >
                clear ({jurisdictions.length})
              </button>
            )}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={jSearch}
                onChange={(e) => setJSearch(e.target.value)}
                placeholder="Search jurisdictions…"
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-border bg-background"
              />
              {jSearch && (
                <button
                  type="button"
                  onClick={() => setJSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear jurisdiction search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        {fwLoading || cfgLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : visibleJurisdictions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No jurisdictions match the search.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {visibleJurisdictions.map((j) => {
              const active = jurisdictions.includes(j);
              return (
                <button
                  key={j}
                  type="button"
                  onClick={() => toggleJurisdiction(j)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    active
                      ? 'bg-primary/10 border-primary/40 text-primary'
                      : 'bg-card border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {JURISDICTION_LABELS[j] ?? j.toUpperCase()}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Frameworks <span className="font-normal normal-case">({activeFrameworks.length} selected)</span>
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {activeFrameworks.length > 0 && (
              <label className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={fOnlySelected}
                  onChange={(e) => setFOnlySelected(e.target.checked)}
                  className="rounded border-border"
                />
                only selected
              </label>
            )}
            {activeFrameworks.length > 0 && (
              <button
                type="button"
                onClick={() => setActiveFrameworks([])}
                className="text-[11px] text-muted-foreground hover:text-foreground shrink-0"
              >
                clear ({activeFrameworks.length})
              </button>
            )}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                placeholder="Search frameworks…"
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-border bg-background"
              />
              {fSearch && (
                <button
                  type="button"
                  onClick={() => setFSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear framework search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        {visibleFrameworks.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No frameworks match the selected jurisdictions.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {visibleFrameworks.map((f) => {
              const checked = activeFrameworks.includes(f.frameworkId);
              return (
                <label
                  key={f.frameworkId}
                  className={cn(
                    'flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors',
                    checked ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/40',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFramework(f.frameworkId)}
                    className="mt-1 rounded border-border"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground">{f.shortName}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {f.category}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-saffron/15 text-saffron">
                        {JURISDICTION_LABELS[f.jurisdiction] ?? f.jurisdiction}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{f.name}</p>
                    {f.description && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{f.description}</p>}
                    {f.url && (
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 mt-1"
                      >
                        reference <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </Card>

      <div>
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save TEP settings
        </Button>
      </div>
    </div>
  );
}
